<template>
  <div class="pengaturan-surat-wrapper">
    <div class="pengaturan-surat-container">
      <!-- Page Title -->
      <h1 class="page-title">Pengaturan Surat</h1>

      <!-- Tanda Tangan Digital Card - Only for NON-admin users -->
      <a-card v-if="canAccessSignature" :bordered="true" class="mb-4">
      <a-form layout="vertical">
        <!-- Tanda Tangan Digital Section -->
        <div class="signature-section">
          <h3>
            Tanda Tangan Digital
          </h3>
          <p class="text-dark">
            Tanda Tangan Digital <span style="color: #ff4d4f; margin-left: 4px">*</span>
          </p>
          <p class="text-muted subtitle">
            Gambar atau unggah tanda tangan yang akan ditampilkan di area tanda tangan surat keluar
          </p>

          <!-- Tab for Gambar and Upload -->
          <a-tabs v-model:activeKey="activeTab" @change="handleTabChange">
            <!-- Gambar Tab (Drawable) -->
            <a-tab-pane key="gambar" tab="Gambar">
              <div class="signature-canvas-wrapper">
                <VueSignaturePad
                  ref="signaturePad"
                  :width="`${canvasWidth}px`"
                  :height="`${canvasHeight}px`"
                  :options="{ onBegin, onEnd, penColor: 'black', minWidth: 1, maxWidth: 2.5 }"
                  class="signature-canvas"
                />
              </div>
              <div class="signature-actions">
                <a-button @click="clearSignature" class="clear-signature-button">
                  BERSIHKAN GAMBAR
                </a-button>
              </div>
            </a-tab-pane>

            <!-- Upload Tab -->
            <a-tab-pane key="upload" tab="Upload">
              <a-upload
                v-model:file-list="fileList"
                name="signatureFile"
                list-type="picture-card"
                class="signature-uploader"
                :before-upload="beforeUpload"
                :max-count="1"
                accept="image/png,image/jpeg,image/jpg"
                @remove="handleRemove"
              >
                <div v-if="!fileList.length">
                  <UploadOutlined />
                  <div style="margin-top: 8px">Upload</div>
                </div>
              </a-upload>
            </a-tab-pane>
          </a-tabs>

          <!-- Confirmation Checkbox -->
          <!-- <a-checkbox v-model:checked="confirmed" class="confirmation-checkbox">
            Saya paham bahwa gambar ini adalah representasi legal dari tanda tangan saya.
          </a-checkbox> -->
          <div class="confirmation-checkbox">
            <a-checkbox v-model:checked="confirmed">
              <span class="checkbox-label">
                Saya paham bahwa gambar ini adalah representasi legal dari tanda tangan saya.
              </span>
            </a-checkbox>
          </div>

          <!-- Save Button -->
          <a-button
            type="primary"
            :loading="loading"
            :disabled="!confirmed || (!hasDrawnSignature && !canvasHasLoadedImage && !fileList.length)"
            @click="saveSignature"
            class="save-button"
          >
            SIMPAN PERUBAHAN
          </a-button>
        </div>
      </a-form>
    </a-card>

    <!-- Stempel Surat Digital Card - Only for Admin -->
    <a-card v-if="canAccessStempelKop" :bordered="true" class="mb-4">
      <a-form layout="vertical">
        <div class="stamp-section">
          <h3>
            Stempel Surat Digital
          </h3>
          <p class="text-dark">
            Stempel Digital <span style="color: #ff4d4f; margin-left: 4px">*</span>
          </p>
          <p class="text-muted subtitle">
            Unggah gambar stempel yang akan digunakan di area tanda tangan surat dengan format PNG (transparan)
          </p>

          <a-upload
            v-model:file-list="stampFileList"
            name="stampFile"
            list-type="picture-card"
            class="stamp-uploader"
            :before-upload="beforeStampUpload"
            :max-count="1"
            accept="image/png"
            @remove="handleStampRemove"
          >
            <div v-if="!stampFileList.length">
              <UploadOutlined />
              <div style="margin-top: 8px">Upload</div>
            </div>
          </a-upload>


          <!-- Save Button for Stamp -->
          <a-button
            type="primary"
            :loading="stampLoading"
            :disabled="!stampFileList.length || !hasNewStampFile"
            @click="confirmStampChange"
            class="save-button"
          >
            SIMPAN PERUBAHAN
          </a-button>
        </div>
      </a-form>
    </a-card>

    <!-- Kop Surat Card - Only for Admin -->
    <a-card v-if="canAccessStempelKop" :bordered="true" class="mb-4">
      <a-form layout="vertical">
        <div class="letterhead-section">
          <h3>
            Kop Surat
          </h3>
          <p
            class="text-dark">Kop Surat <span style="color: #ff4d4f; margin-left: 4px">*</span>
          </p>
          <p class="text-muted subtitle">
            Unggah kop surat yang akan ditampilkan di bagian header surat keluar
          </p>

          <a-upload
            v-model:file-list="letterheadFileList"
            name="letterheadFile"
            list-type="picture-card"
            class="letterhead-uploader"
            :before-upload="beforeLetterheadUpload"
            :max-count="1"
            accept="image/png,image/jpeg,image/jpg"
            @remove="handleLetterheadRemove"
          >
            <div v-if="!letterheadFileList.length">
              <UploadOutlined />
              <div style="margin-top: 8px">Upload</div>
            </div>
          </a-upload>

          <!-- Save Button for Letterhead -->
          <a-button
            type="primary"
            :loading="letterheadLoading"
            :disabled="!letterheadFileList.length || !hasNewLetterheadFile"
            @click="confirmLetterheadChange"
            class="save-button"
          >
            SIMPAN PERUBAHAN
          </a-button>
        </div>
      </a-form>
    </a-card>
  </div>

  <!-- Confirmation Modal for Stempel -->
  <a-modal
    v-model:visible="showStempelConfirmModal"
    :closable="true"
    :footer="null"
    width="500px"
    centered
  >
    <div class="confirmation-modal">
      <h2 class="modal-title">Ubah Stempel?</h2>
      <p class="modal-description">
        Mohon cek kembali gambar stempel sebelum menyimpan perubahan. Perubahan stempel surat hanya akan memengaruhi surat keluar yang masih berstatus Draft.
      </p>
      <div class="modal-actions">
        <a-button @click="showStempelConfirmModal = false" class="cancel-button">
          KEMBALI
        </a-button>
        <a-button type="primary" @click="handleConfirmStampChange" :loading="stampLoading" class="confirm-button">
          YA, SIMPAN
        </a-button>
      </div>
    </div>
  </a-modal>

  <!-- Confirmation Modal for Kop Surat -->
  <a-modal
    v-model:visible="showKopSuratConfirmModal"
    :closable="true"
    :footer="null"
    width="500px"
    centered
  >
    <div class="confirmation-modal">
      <h2 class="modal-title">Ubah Kop Surat?</h2>
      <p class="modal-description">
        Mohon cek kembali gambar kop surat sebelum menyimpan perubahan. Perubahan kop surat hanya akan memengaruhi surat keluar yang masih berstatus Draft.
      </p>
      <div class="modal-actions">
        <a-button @click="showKopSuratConfirmModal = false" class="cancel-button">
          KEMBALI
        </a-button>
        <a-button type="primary" @click="handleConfirmLetterheadChange" :loading="letterheadLoading" class="confirm-button">
          YA, SIMPAN
        </a-button>
      </div>
    </div>
  </a-modal>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, onUnmounted, nextTick, watch, computed } from 'vue'
import { notification } from 'ant-design-vue'
import { UploadOutlined, ClearOutlined } from '@ant-design/icons-vue'
import { VueSignaturePad } from 'vue-signature-pad'
import { signatureAPI, suratSettingsAPI, getFileUrl } from '@/services/axios/perki'
import { useUnsavedChangesWarning } from '@/composables/useUnsavedChangesWarning'

export default defineComponent({
  name: 'PengaturanSurat',
  components: {
    VueSignaturePad,
    UploadOutlined,
    // ClearOutlined,
  },
  setup() {
    const activeTab = ref('gambar')
    const signaturePad = ref(null)
    const fileList = ref([])
    const confirmed = ref(false)
    const loading = ref(false)
    const hasDrawnSignature = ref(false)
    const currentSignature = ref(null)
    const canvasHasLoadedImage = ref(false) // Track if canvas has a loaded signature
    const isLoadingSignatureImage = ref(false) // Prevent re-draw loops during image load

    // Responsive canvas dimensions
    // Frontend: Full-width responsive canvas for better UX (variable based on screen size)
    // Backend: Crops whitespace, scales to max 600×300 preserving aspect ratio (variable output size)
    const canvasWidth = ref(600)
    const canvasHeight = ref(300)

    // Resize debounce timer
    let resizeDebounceTimer = null

    // State for Stamp (Stempel)
    const stampFileList = ref([])
    const stampLoading = ref(false)
    const stampConfirmed = ref(false)
    const currentStamp = ref(null)
    const showStempelConfirmModal = ref(false)
    const hasNewStampFile = ref(false)

    // State for Letterhead (Kop Surat)
    const letterheadFileList = ref([])
    const letterheadLoading = ref(false)
    const letterheadConfirmed = ref(false)
    const currentLetterhead = ref(null)
    const showKopSuratConfirmModal = ref(false)
    const hasNewLetterheadFile = ref(false)

    // Role-based access control
    // Admin: can access Stempel + Kop Surat cards, NOT signature card
    // Non-admin (sekretaris, etc.): can access ONLY Signature card
    const canAccessSignature = ref(false)
    const canAccessStempelKop = ref(false)

    // 1. New Helper: Scales canvas for Retina/High-DPI screens
    const scaleCanvas = () => {
      const pad = signaturePad.value
      if (!pad) return

      const canvas = pad.$el.querySelector('canvas')
      if (!canvas) return

      // Get the device pixel ratio (1 for standard, 2 or 3 for retina)
      const ratio = Math.max(window.devicePixelRatio || 1, 1)

      // Get current CSS width/height (the visual size we calculated)
      // We parseInt because the ref might be a number or string
      const width = typeof canvasWidth.value === 'number' ? canvasWidth.value : parseInt(canvasWidth.value)
      const height = typeof canvasHeight.value === 'number' ? canvasHeight.value : parseInt(canvasHeight.value)

      // IMPORTANT: Save data before resizing, or the canvas will wipe!
      const data = pad.toData()

      // 2. Set the "Internal" size (multiply by ratio)
      canvas.width = width * ratio
      canvas.height = height * ratio

      // 3. Scale the context to match
      // This tells the canvas: "When I draw 1 pixel, actually use 'ratio' pixels"
      const ctx = canvas.getContext('2d')
      ctx.scale(ratio, ratio)

      // 4. Restore the visual data
      // We need to clear first because the resize might have artifacts
      pad.clearSignature()
      pad.fromData(data)
    }

    // Calculate responsive canvas size - maintain good aspect ratio for signing
    // Uses debounce to prevent excessive redraws during resize
    // Refine the responsive calculation
    const updateCanvasSize = () => {
      if (resizeDebounceTimer) {
        clearTimeout(resizeDebounceTimer)
      }

      resizeDebounceTimer = setTimeout(() => {
        const screenWidth = window.innerWidth
        let availableWidth;
        let newHeight;

        if (screenWidth < 576) {
          // Mobile (Phone)
          // Calculation: Screen - (Container Pad 32px + Card Pad 48px + Wrapper Pad 16px + Safety 14px)
          availableWidth = screenWidth - 110
          newHeight = 180 // Slightly shorter to fit mobile screens better
        } else if (screenWidth < 992) {
          // Tablet
          // Calculation: Screen - (Container Pad 48px + Card Pad 48px + Wrapper Pad 16px + Safety 18px)
          availableWidth = screenWidth - 130
          newHeight = 220
        } else {
          // Desktop
          availableWidth = 700
          newHeight = 300
        }

        // Ensure we don't go below a minimum usable size
        const newWidth = Math.max(availableWidth, 240)

        // Only update if dimensions actually changed
        if (canvasWidth.value !== newWidth || canvasHeight.value !== newHeight) {
          canvasWidth.value = newWidth
          canvasHeight.value = newHeight

          // WAIT for Vue to update the DOM (width/height props), THEN scale for DPI
          nextTick(() => {
            scaleCanvas()

            // If we have an image loaded (not drawn), reload it
            if (currentSignature.value && activeTab.value === 'gambar' && canvasHasLoadedImage.value) {
               setTimeout(() => loadSignatureIntoCanvas(), 100)
            }
          })
        }
      }, 150)
    }

    // Check user role on mount and set access permissions

    const checkUserRole = () => {
      try {
        const perkiUser = localStorage.getItem('perki_user')
        const perkiRole = localStorage.getItem('perki_role')

        let role = ''
        if (perkiRole) {
          role = perkiRole
        } else if (perkiUser) {
          const user = JSON.parse(perkiUser)
          role = user.role || ''
        }

        // Admin and super_admin can access Stempel + Kop Surat
        const isAdmin = role === 'admin' || role === 'super_admin'

        // Non-admin users can access Signature card
        canAccessSignature.value = !isAdmin
        canAccessStempelKop.value = isAdmin

        console.log('Role check:', { role, canAccessSignature: canAccessSignature.value, canAccessStempelKop: canAccessStempelKop.value })
      } catch (error) {
        console.error('Error checking user role:', error)
        // Default: show signature only (safer default for regular users)
        canAccessSignature.value = true
        canAccessStempelKop.value = false
      }
    }

    const onBegin = () => {
      hasDrawnSignature.value = true
    }

    const onEnd = () => {
      hasDrawnSignature.value = true
    }

    const handleTabChange = key => {
      activeTab.value = key

      // Load existing signature into the appropriate component when switching tabs
      if (currentSignature.value) {
        if (key === 'gambar') {
          // Load signature into canvas - use nextTick to ensure tab panel is rendered
          fileList.value = [] // Clear upload list first
          nextTick(() => {
            setTimeout(() => loadSignatureIntoCanvas(), 150)
          })
        } else if (key === 'upload') {
          // Load uploaded signature into file list if it was uploaded type
          if (currentSignature.value.signatureType === 'upload') {
            loadSignatureIntoFileList()
          }
          clearSignature() // Clear canvas
        }
      } else {
        // No existing signature, just clear
        if (key === 'gambar') {
          fileList.value = []
        } else {
          clearSignature()
        }
      }
    }

    const clearSignature = () => {
      if (signaturePad.value) {
        signaturePad.value.clearSignature()
        hasDrawnSignature.value = false
        canvasHasLoadedImage.value = false
        isLoadingSignatureImage.value = false
      }
    }

    const beforeUpload = file => {
      const isImage = file.type === 'image/png' || file.type === 'image/jpeg'
      if (!isImage) {
        notification.error({
          message: 'Error',
          description: 'Hanya file PNG atau JPEG yang diperbolehkan!',
        })
        return false
      }

      return false // Prevent auto upload
    }

    const handleRemove = () => {
      fileList.value = []
    }

    const getSignatureUrl = path => {
      return getFileUrl(path)
    }

    const fetchCurrentSignature = async () => {
      try {
        const response = await signatureAPI.getMySignature()
        // Backend returns response.data.data (not result)
        if (response.data && response.data.data) {
          currentSignature.value = response.data.data
          // Don't automatically load into components here
          // Loading will be handled by caller (onMounted, save handlers, etc)
        }
      } catch (error) {
        // No signature yet, that's okay
        if (error.response?.status !== 404) {
          console.error('Error fetching signature:', error)
        }
      }
    }

        /**
     * Load the saved signature image into the canvas with proper aspect-fit scaling.
     *
     * Problem solved:
     * - Backend returns a cropped image with variable dimensions (aspect-fit processed)
     * - Frontend canvas is larger and has different aspect ratio
     * - Previously: drawImage stretched the image to fill, causing distortion
     * - Now: Scale proportionally (aspect-fit) and center in canvas
     */
    const loadSignatureIntoCanvas = () => {
if (!currentSignature.value || !signaturePad.value) {
        return
      }

      if (isLoadingSignatureImage.value) return
      isLoadingSignatureImage.value = true

      const img = new Image()
      img.crossOrigin = 'anonymous'

      img.onload = () => {
        try {
          if (!signaturePad.value) return

          const canvas = signaturePad.value.$el?.querySelector('canvas')
          if (!canvas) return

          const ctx = canvas.getContext('2d')

          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high'; // Critical for Chrome/Edge

          // 1. Get the device pixel ratio (usually 1 on standard screens, 2 on Retina/High DPI)
          const ratio = Math.max(window.devicePixelRatio || 1, 1)

          // 2. Calculate "Logical" dimensions by dividing physical pixels by the ratio.
          //    This compensates for the scaling matrix applied by signature_pad.
          const cw = canvas.width / ratio
          const ch = canvas.height / ratio

          // 3. Clear the canvas using logical dimensions
          //    (0,0,cw,ch) * ratio = clears the full physical canvas
          ctx.clearRect(0, 0, cw, ch)

          // 4. Calculate aspect-fit scale based on LOGICAL dimensions
          const scaleX = cw / img.width
          const scaleY = ch / img.height
          const scale = Math.min(scaleX, scaleY)

          const scaledWidth = img.width * scale
          const scaledHeight = img.height * scale

          // 5. Center the image in the LOGICAL space
          const offsetX = (cw - scaledWidth) / 2
          const offsetY = (ch - scaledHeight) / 2

          // 6. Draw the image. The context's internal scale (ratio) will
          //    automatically multiply these values back up to physical pixels.
          ctx.drawImage(
            img,
            0, 0, img.width, img.height,      // Source: Full image
            offsetX, offsetY,                 // Dest: Logical position
            scaledWidth, scaledHeight,         // Dest: Logical size
          )

          // 7. Mark that we successfully loaded an image
          canvasHasLoadedImage.value = true
          hasDrawnSignature.value = false // Not a new drawing, just loaded

        } catch (error) {
          console.error('loadSignatureIntoCanvas: Error drawing image:', error)
        } finally {
          isLoadingSignatureImage.value = false
        }
      }

      img.onerror = (error) => {
        console.error('loadSignatureIntoCanvas: Failed to load image:', error)
        isLoadingSignatureImage.value = false
      }

      // Add timestamp to prevent browser caching old versions
      const imageUrl = `${getFileUrl(currentSignature.value.signaturePath)}?t=${new Date().getTime()}`
      console.log('loadSignatureIntoCanvas: Loading from URL:', imageUrl)
      img.src = imageUrl
    }

    // Get raw signature canvas data
    // Backend handles processing: crops whitespace, scales if needed (max 600×300),
    // preserves aspect ratio, and adds padding - creating variable-sized output
    const getSignatureData = (canvas) => {
      // Simply return the raw canvas data - backend will handle all processing
      return canvas.toDataURL('image/png')
    }

    const loadSignatureIntoFileList = () => {
      if (!currentSignature.value) return

      // Create a file list item for the current uploaded signature
      fileList.value = [
        {
          uid: currentSignature.value.id,
          name: currentSignature.value.signaturePath.split('/').pop(),
          status: 'done',
          url: getFileUrl(currentSignature.value.signaturePath),
        },
      ]
    }

    const saveSignature = async () => {
      if (!confirmed.value) {
        notification.warning({
          message: 'Peringatan',
          description: 'Anda harus menyetujui konfirmasi terlebih dahulu',
        })
        return
      }

      loading.value = true

      try {
        if (activeTab.value === 'gambar') {
          // Save drawable signature
          if (!signaturePad.value) {
            notification.error({
              message: 'Error',
              description: 'Signature pad not initialized',
            })
            loading.value = false
            return
          }

          let signatureData

          // Get canvas data directly to avoid progressive scaling
          const canvas = signaturePad.value.$el.querySelector('canvas')
          if (!canvas) {
            notification.error({
              message: 'Error',
              description: 'Canvas not found',
            })
            loading.value = false
            return
          }

          // Check if canvas is empty
          const ctx = canvas.getContext('2d')
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
          const isCanvasEmpty = !imageData.data.some(channel => channel !== 0)

          if (isCanvasEmpty) {
            notification.warning({
              message: 'Peringatan',
              description: 'Silakan buat tanda tangan terlebih dahulu',
            })
            loading.value = false
            return
          }

          // Get raw signature data - backend will process it
          signatureData = getSignatureData(canvas)

          if (!signatureData) {
            notification.warning({
              message: 'Peringatan',
              description: 'Tidak dapat memproses tanda tangan',
            })
            loading.value = false
            return
          }

          console.log('Signature save attempt:', { dataLength: signatureData?.length })

          // Send base64 data to backend
          const response = await signatureAPI.uploadBase64({
            signatureType: 'image',
            signatureData: signatureData,
          })

          if (response.data.status === 'success' || response.data.status.includes('success')) {
            notification.success({
              message: 'Berhasil',
              description: 'Tanda tangan berhasil diubah',
            })
            resetUnsavedChanges()
            await fetchCurrentSignature()
            // Reset canvas state and reload the centered signature
            canvasHasLoadedImage.value = false
            hasDrawnSignature.value = false
            confirmed.value = false
            // Wait a bit for the fetch to complete, then reload
            setTimeout(() => loadSignatureIntoCanvas(), 200)
          }
        } else {
          // Save uploaded signature
          if (!fileList.value.length) {
            notification.warning({
              message: 'Peringatan',
              description: 'Silakan pilih file terlebih dahulu',
            })
            loading.value = false
            return
          }

          const formData = new FormData()
          formData.append('signatureType', 'upload')
          formData.append('signatureFile', fileList.value[0].originFileObj)

          const response = await signatureAPI.create(formData)

          if (response.data.status === 'success' || response.data.status.includes('success')) {
            notification.success({
              message: 'Berhasil',
              description: 'Tanda tangan berhasil diubah',
            })
            resetUnsavedChanges()
            await fetchCurrentSignature()
            // Reload the file list with the saved signature
            loadSignatureIntoFileList()
            confirmed.value = false
          }
        }
      } catch (error) {
        console.error('Error saving signature:', error)
        notification.error({
          message: 'Error',
          description:
            error.response?.data?.message || 'Gagal menyimpan tanda tangan. Silakan coba lagi.',
        })
      } finally {
        loading.value = false
      }
    }

    // Stamp (Stempel) Functions
    const beforeStampUpload = file => {
      const isPng = file.type === 'image/png'
      if (!isPng) {
        notification.error({
          message: 'Error',
          description: 'Hanya file PNG yang diperbolehkan untuk stempel!',
        })
        return false
      }

      return false // Prevent auto upload
    }

    const handleStampRemove = () => {
      stampFileList.value = []
      hasNewStampFile.value = false
    }

    // Watch for new stamp file upload
    watch(stampFileList, (newList) => {
      if (newList.length > 0 && newList[0].originFileObj) {
        hasNewStampFile.value = true
      } else {
        hasNewStampFile.value = false
      }
    }, { deep: true })

    const fetchCurrentStamp = async () => {
      try {
        const response = await suratSettingsAPI.get()
        if (response.data && response.data.data) {
          const settings = response.data.data
          if (settings.stempelPath) {
            currentStamp.value = { stampPath: settings.stempelPath }
            loadStampIntoFileList()
          }
        }
      } catch (error) {
        if (error.response?.status !== 404) {
          console.error('Error fetching stamp:', error)
        }
      }
    }

    const loadStampIntoFileList = () => {
      if (!currentStamp.value) return

      // Create a file list item for the current stamp
      stampFileList.value = [
        {
          uid: '-1',
          name: currentStamp.value.stampPath.split('/').pop(),
          status: 'done',
          url: getFileUrl(currentStamp.value.stampPath),
        },
      ]
      hasNewStampFile.value = false // Existing file, not new
    }

    const confirmStampChange = () => {
      console.log('confirmStampChange called', {
        hasNewStampFile: hasNewStampFile.value,
        stampFileList: stampFileList.value,
        showStempelConfirmModal: showStempelConfirmModal.value,
      })
      showStempelConfirmModal.value = true
      console.log('Modal should show now:', showStempelConfirmModal.value)
    }

    const handleConfirmStampChange = async () => {
      console.log('handleConfirmStampChange called')
      showStempelConfirmModal.value = false
      await saveStamp()
    }

    const saveStamp = async () => {
      if (!stampFileList.value.length) {
        notification.warning({
          message: 'Peringatan',
          description: 'Silakan pilih file stempel terlebih dahulu',
        })
        return
      }

      // Check if this is a new upload or existing file
      const fileItem = stampFileList.value[0]
      if (!fileItem.originFileObj) {
        // This is an existing file that was loaded, not a new upload
        notification.info({
          message: 'Info',
          description: 'File ini sudah tersimpan. Silakan upload file baru jika ingin mengubah.',
        })
        return
      }

      stampLoading.value = true

      try {
        const file = fileItem.originFileObj

        // Convert to base64
        const base64Data = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(file)
        })

        const response = await suratSettingsAPI.uploadStempel({
          stempelData: base64Data,
        })

        if (response.data.status === 'success' || response.data.status.includes('success')) {
          notification.success({
            message: 'Berhasil',
            description: 'Stempel berhasil diubah',
          })
          resetUnsavedChanges()
          await fetchCurrentStamp()
          hasNewStampFile.value = false
          // loadStampIntoFileList() is called inside fetchCurrentStamp()
        }
      } catch (error) {
        console.error('Error saving stamp:', error)
        notification.error({
          message: 'Error',
          description: error.response?.data?.message || 'Gagal menyimpan stempel. Silakan coba lagi.',
        })
      } finally {
        stampLoading.value = false
      }
    }

    // Letterhead (Kop Surat) Functions
    const beforeLetterheadUpload = file => {
      const isImage = file.type === 'image/png' || file.type === 'image/jpeg'
      if (!isImage) {
        notification.error({
          message: 'Error',
          description: 'Hanya file PNG atau JPEG yang diperbolehkan!',
        })
        return false
      }

      return false // Prevent auto upload
    }

    const handleLetterheadRemove = () => {
      letterheadFileList.value = []
      hasNewLetterheadFile.value = false
    }

    // Watch for new letterhead file upload
    watch(letterheadFileList, (newList) => {
      if (newList.length > 0 && newList[0].originFileObj) {
        hasNewLetterheadFile.value = true
      } else {
        hasNewLetterheadFile.value = false
      }
    }, { deep: true })

    const fetchCurrentLetterhead = async () => {
      try {
        const response = await suratSettingsAPI.get()
        if (response.data && response.data.data) {
          const settings = response.data.data
          if (settings.kopSuratPath) {
            currentLetterhead.value = { letterheadPath: settings.kopSuratPath }
            loadLetterheadIntoFileList()
          }
        }
      } catch (error) {
        if (error.response?.status !== 404) {
          console.error('Error fetching letterhead:', error)
        }
      }
    }

    const loadLetterheadIntoFileList = () => {
      if (!currentLetterhead.value) return

      // Create a file list item for the current letterhead
      letterheadFileList.value = [
        {
          uid: '-1',
          name: currentLetterhead.value.letterheadPath.split('/').pop(),
          status: 'done',
          url: getFileUrl(currentLetterhead.value.letterheadPath),
        },
      ]
      hasNewLetterheadFile.value = false // Existing file, not new
    }

    const confirmLetterheadChange = () => {
      console.log('confirmLetterheadChange called', {
        hasNewLetterheadFile: hasNewLetterheadFile.value,
        letterheadFileList: letterheadFileList.value,
        showKopSuratConfirmModal: showKopSuratConfirmModal.value,
      })
      showKopSuratConfirmModal.value = true
      console.log('Modal should show now:', showKopSuratConfirmModal.value)
    }

    const handleConfirmLetterheadChange = async () => {
      console.log('handleConfirmLetterheadChange called')
      showKopSuratConfirmModal.value = false
      await saveLetterhead()
    }

    const saveLetterhead = async () => {
      if (!letterheadFileList.value.length) {
        notification.warning({
          message: 'Peringatan',
          description: 'Silakan pilih file kop surat terlebih dahulu',
        })
        return
      }

      // Check if this is a new upload or existing file
      const fileItem = letterheadFileList.value[0]
      if (!fileItem.originFileObj) {
        // This is an existing file that was loaded, not a new upload
        notification.info({
          message: 'Info',
          description: 'File ini sudah tersimpan. Silakan upload file baru jika ingin mengubah.',
        })
        return
      }

      letterheadLoading.value = true

      try {
        const file = fileItem.originFileObj

        // Convert to base64
        const base64Data = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(file)
        })

        const response = await suratSettingsAPI.uploadKopSurat({
          kopSuratData: base64Data,
        })

        if (response.data.status === 'success' || response.data.status.includes('success')) {
          notification.success({
            message: 'Berhasil',
            description: 'Kop surat berhasil diubah',
          })
          resetUnsavedChanges()
          await fetchCurrentLetterhead()
          hasNewLetterheadFile.value = false
          // loadLetterheadIntoFileList() is called inside fetchCurrentLetterhead()
        }
      } catch (error) {
        console.error('Error saving letterhead:', error)
        notification.error({
          message: 'Error',
          description: error.response?.data?.message || 'Gagal menyimpan kop surat. Silakan coba lagi.',
        })
      } finally {
        letterheadLoading.value = false
      }
    }

    onMounted(async () => {
      checkUserRole()

      // Run initial size calculat7ion immediately
      // We simulate the logic from updateCanvasSize without the debounce
      // Initial calculation
      updateCanvasSize()

      // Force a scale check immediately after mount just in case
      nextTick(() => scaleCanvas())

      // Add resize listener for responsive canvas
      window.addEventListener('resize', updateCanvasSize)

      // Only fetch signature if user can access it
      if (canAccessSignature.value) {
        await fetchCurrentSignature()

        // Set active tab based on existing signature type
        if (currentSignature.value) {
          if (currentSignature.value.signatureType === 'image') {
            activeTab.value = 'gambar'
            // Use nextTick to ensure Vue has rendered the canvas component,
            // then add small delay for VueSignaturePad internal initialization
            await nextTick()
            setTimeout(() => loadSignatureIntoCanvas(), 200)
          } else if (currentSignature.value.signatureType === 'upload') {
            activeTab.value = 'upload'
            loadSignatureIntoFileList()
          }
        }
      }

      // Only fetch stempel/kop if user can access them
      if (canAccessStempelKop.value) {
        fetchCurrentStamp()
        fetchCurrentLetterhead()
      }
    })

    onUnmounted(() => {
      window.removeEventListener('resize', updateCanvasSize)
      // Clear any pending debounce timer
      if (resizeDebounceTimer) {
        clearTimeout(resizeDebounceTimer)
        resizeDebounceTimer = null
      }
    })

    return {
      activeTab,
      signaturePad,
      fileList,
      confirmed,
      loading,
      hasDrawnSignature,
      canvasHasLoadedImage,
      currentSignature,
      canvasWidth,
      canvasHeight,
      onBegin,
      onEnd,
      handleTabChange,
      clearSignature,
      beforeUpload,
      handleRemove,
      getSignatureUrl: getFileUrl,
      saveSignature,
      // Role-based access
      canAccessSignature,
      canAccessStempelKop,
      // Stamp (Stempel) returns
      stampFileList,
      stampLoading,
      stampConfirmed,
      currentStamp,
      beforeStampUpload,
      handleStampRemove,
      saveStamp,
      showStempelConfirmModal,
      hasNewStampFile,
      confirmStampChange,
      handleConfirmStampChange,
      // Letterhead (Kop Surat) returns
      letterheadFileList,
      letterheadLoading,
      letterheadConfirmed,
      currentLetterhead,
      beforeLetterheadUpload,
      handleLetterheadRemove,
      saveLetterhead,
      showKopSuratConfirmModal,
      hasNewLetterheadFile,
      confirmLetterheadChange,
      handleConfirmLetterheadChange,
      // Shared
      getFileUrl,
    }
  },
})
</script>

<style lang="scss" scoped>
.pengaturan-surat-container {
  padding: 24px;

  .page-title {
    font-size: 30px;
    font-weight: bold;
    color: black;
    margin-bottom: 24px;
    margin-top: 0;
  }

  .clear-signature-button {
    border: 2px solid #222;
    background: #fff;
    color: #222;
    border-radius: 25px;
    height: 40px;
    font-weight: 600;
    font-size: 16px;
    box-shadow: none;
    margin-bottom: 16px;

    &:hover,
    &:focus {
      border-color: #1d2671;
      color: #1d2671;
      background: #f6f8fb;
    }
  }


  .mb-4 {
    margin-bottom: 24px;
  }

  .mb-3 {
    margin-bottom: 16px;
  }

  .current-signature-info {
    margin-bottom: 16px;
  }

  .signature-section,
  .stamp-section,
  .letterhead-section {
    h3 {
      font-size: 24px;
      font-weight: 600;
      margin-bottom: 8px;
    }

    .text-dark {
      color: #262626;
      font-weight: 600;
      margin-bottom: 4px;
      margin-top: 0;
    }

    .text-muted {
      color: #8c8c8c;
      font-size: 14px;

      &.subtitle {
        margin-bottom: 12px;
        margin-top: 0;
      }
    }

    .signature-canvas-wrapper {
      border: 2px dashed #d9d9d9;
      border-radius: 8px;
      padding: 8px;
      background: #fafafa;
      margin-bottom: 12px;

      // Ensure these Flexbox properties are present to center the canvas
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      overflow: hidden; // Prevents the dashed border from breaking if canvas is 1px off

      .signature-canvas {
        background: white;
        border-radius: 4px;
        border: 1px solid #d9d9d9;
        display: block;
        cursor: crosshair;
        touch-action: none;

        // IMPORTANT: Do NOT set width/height 100% on the canvas!
        // Canvas must have matching CSS size and internal pixel size.
        // VueSignaturePad sets this via the :width/:height props.
        // Forcing 100% CSS causes mouse position mismatch.
      }
    }

    .signature-actions {
      margin-bottom: 16px;
      margin-top: 0;

      .mr-2 {
        margin-right: 8px;
      }
    }

    .signature-uploader,
    .stamp-uploader,
    .letterhead-uploader {
      margin-bottom: 12px;
      margin-top: 0;
    }

    .upload-hint {
      color: #8c8c8c;
      font-size: 12px;
      margin-bottom: 24px;
    }

    .current-signature-preview,
    .current-preview {
      margin-top: 24px;

      .preview-wrapper {
        text-align: center;
        padding: 16px;
        background: #fafafa;
        border: 1px solid #d9d9d9;
        border-radius: 8px;

        img {
          max-width: 300px;
          max-height: 150px;
          object-fit: contain;
        }

        .signature-type-label {
          margin-top: 8px;
          color: #8c8c8c;
          font-size: 12px;
        }
      }
    }

    .confirmation-checkbox {
      display: block;
      margin-top: 0px;
      margin-bottom: 16px;

      .checkbox-label {
          font-size: 14px;
          font-weight: 500;
          color: #262626;
          margin-left: 8px;
        }

        :deep(.ant-checkbox-checked .ant-checkbox-inner) {
          background-color: #e10f28;
          border-color: #e10f28;
        }

        :deep(.ant-checkbox-wrapper:hover .ant-checkbox-inner) {
          border-color: #e10f28;
        }
    }

    .save-button {
     border-radius: 50px;
      font-size: 14px;
      font-weight: 700;
      border: 2px solid #e10f28;
      background: #e10f28;
      color: white;
      padding: 6px 16px;
      min-height: auto;
      height: 42px;
      line-height: 1.5;
      transition: all 0.3s;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      box-shadow: none;

      &:hover:not(:disabled) {
        background: white !important;
        border-color: #e10f28 !important;
        color: #e10f28 !important;
      }

      &:disabled {
        border-color: #d9d9d9;
        background: #f5f5f5;
        color: #d9d9d9;
      }
    }
  }
}

// Modal Styles
.confirmation-modal {
  padding: 8px 0;

  .modal-title {
    font-size: 24px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 16px;
    margin-top: 0;
  }

  .modal-description {
    font-size: 14px;
    color: #595959;
    line-height: 1.6;
    margin-bottom: 24px;
  }

  .modal-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;

    .cancel-button {
      flex : 1;
      height: 44px;
      padding: 0 32px;
      font-size: 16px;
      font-weight: 600;
      border-radius: 25px;
      border: 2px solid #222;
      background: #fff;
      color: #222;
      letter-spacing: 0.5px;

      &:hover {
        border-color: #1d2671;
        color: #1d2671;
        background: #f6f8fb;
      }
    }

    .confirm-button {
      flex : 1;
      height: 44px;
      padding: 0 32px;
      font-size: 16px;
      font-weight: 600;
      border-radius: 25px;
      background: #E10F28;
      border: none;
      color: white;
      letter-spacing: 0.5px;

      &:hover:not(:disabled) {
        opacity: 0.9;
      }

      &:disabled {
        opacity: 0.6;
      }
    }
  }
}
</style>
